import React, { useState, useEffect, useRef } from 'react';
import VirtualKeyboard from './VirtualKeyboard';
import metricsTracker from '../utils/metricsTracker';

const Multiplication = ({ updateMetrics }) => {
  const [num1, setNum1] = useState('');
  const [num2, setNum2] = useState('');
  const [userAnswer, setUserAnswer] = useState([]);
  const [partialProducts, setPartialProducts] = useState([]);
  const [carryOver, setCarryOver] = useState([]);
  const [showSolution, setShowSolution] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);
  const [customNumbers, setCustomNumbers] = useState({ num1: '', num2: '' });
  const [useCustomNumbers, setUseCustomNumbers] = useState(false);
  const [gradeLevel, setGradeLevel] = useState('1');
  const [questionNumber, setQuestionNumber] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [solutionSteps, setSolutionSteps] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Button state management
  const [buttonsState, setButtonsState] = useState({
    checkAnswerDisabled: false,
    showSolutionDisabled: true
  });
  
  // Virtual keyboard state
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardPosition, setKeyboardPosition] = useState({ x: 200, y: 100 });
  const [activeInput, setActiveInput] = useState(null);
  const mathProblemRef = useRef(null);

  // Level difficulty ranges - properly aligned with "Upto X digits"
  const gradeRanges = {
    '1': { min: 1, max: 99 },        // Upto 2 digits
    '2': { min: 10, max: 999 },      // Upto 3 digits
    '3': { min: 100, max: 9999 },    // Upto 4 digits
    '4': { min: 1000, max: 99999 },  // Upto 5 digits
    '5': { min: 10000, max: 999999 }, // Upto 6 digits
    '6': { min: 100000, max: 9999999 }, // Upto 7 digits
    '7': { min: 1000000, max: 99999999 }, // Upto 8 digits
    '8': { min: 10000000, max: 999999999 } // Upto 9 digits
  };

  // Calculate paper savings
  const calculatePaperSavings = () => {
    const linesPerQuestion = 5; // Each multiplication question takes about 5 lines
    const validTotalQuestions = Number.isFinite(totalQuestions) ? totalQuestions : 0;
    const totalLines = validTotalQuestions * linesPerQuestion;
    const papersSaved = Math.ceil(totalLines / 25); // 25 lines per A4 sheet
    return { papersSaved, totalLines };
  };

  // Calculate the required length for answer
  const calculateAnswerLength = () => {
    const a = parseInt(num1) || 0;
    const b = parseInt(num2) || 0;
    const result = a * b;
    return result.toString().length;
  };

  // Generate random numbers for the problem
  const generateProblem = (isInitialLoad = false) => {
    if (useCustomNumbers && customNumbers.num1 && customNumbers.num2) {
      setNum1(customNumbers.num1);
      setNum2(customNumbers.num2);
    } else {
      const range = gradeRanges[gradeLevel];
      const newNum1 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
      const newNum2 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
      
      setNum1(newNum1.toString());
      setNum2(newNum2.toString());
    }
    
    setUserAnswer([]);
    setPartialProducts([]);
    setCarryOver([]);
    setShowSolution(false);
    setSolutionSteps(null);
    setIsCorrect(null);
    setMessage('');
    setValidationErrors([]);
    
    // Reset button states for new problem
    setButtonsState({
      checkAnswerDisabled: false,
      showSolutionDisabled: true
    });
    
    // Only increment question number if it's not the initial load
    if (!isInitialLoad) {
    setQuestionNumber(prev => prev + 1);
    }
    setTotalQuestions(prev => prev + 1);
    
    // Report metrics for new problem
    if (updateMetrics) {
      const { papersSaved } = calculatePaperSavings();
      updateMetrics(papersSaved);
    }
  };

  // Handle digit input changes
  const handleDigitChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newAnswer = [...userAnswer];
      newAnswer[index] = value;
      setUserAnswer(newAnswer);
    }
  };

  // Handle partial product input
  const handlePartialProductChange = (rowIndex, colIndex, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newPartialProducts = [...partialProducts];
      if (!newPartialProducts[rowIndex]) {
        newPartialProducts[rowIndex] = [];
      }
      newPartialProducts[rowIndex][colIndex] = value;
      setPartialProducts(newPartialProducts);
    }
  };

  // Handle carry over input
  const handleCarryChange = (colIndex, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCarry = [...carryOver];
      newCarry[colIndex] = value;
      setCarryOver(newCarry);
    }
  };

  // Handle input focus for virtual keyboard - using proven logic from Addition/Subtraction
  const handleInputFocus = (index, type, rowIndex = null) => {
    console.log('Input focused:', { index, type, rowIndex });
    setActiveInput({ index, type, rowIndex });
    setKeyboardVisible(true);
    
    // Position keyboard just next to the input (using proven logic from Addition/Subtraction)
    let inputElement;
    if (type === 'partial') {
      // For partial products, the data-index includes rowIndex
      inputElement = document.querySelector(`input[data-index="partial-${rowIndex}-${index}"][data-type="partial"]`);
    } else {
      // For answer and carry fields, use the standard format
      inputElement = document.querySelector(`input[data-index="${type}-${index}"][data-type="${type}"]`);
    }
    
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      const mathProblemRect = mathProblemRef.current?.getBoundingClientRect();
      
      if (mathProblemRect) {
        // Calculate position based on place value - move left for higher place values
        const placeValue = answerLength - index - 1; // 0 = ones, 1 = tens, 2 = hundreds, etc.
        
        // Base position: just next to input (5px offset) - using proven logic
        let x = rect.right - mathProblemRect.left + 5;
        
        // Move left for higher place values to avoid hiding questions
        if (placeValue >= 1) { // tens or higher
          x -= (placeValue * 15); // Move left by 15px per place value
        }
        
        // Ensure keyboard doesn't go too far left
        x = Math.max(x, 50);
        
        // Move keyboard down to avoid hiding the problem - using proven logic
        let y = rect.top - mathProblemRect.top;
        
        // If this is a carry field, move keyboard down more
        if (type === 'carry') {
          y += 60; // Move down by 60px for carry fields
        } else {
          y += 40; // Move down by 40px for other fields (answer and partial)
        }
        
        setKeyboardPosition({ x, y });
      }
    }
  };

  const handleInputBlur = () => {
    // Don't hide keyboard, don't clear active input to allow virtual keyboard input
    // setActiveInput(null); // Commented out to allow virtual keyboard input
  };

  // Virtual keyboard handlers
  const handleVirtualKeyPress = (key) => {
    console.log('Virtual key pressed:', key, 'Active input:', activeInput);
    
    // Use activeInput state which is set when input is focused
    if (activeInput) {
      if (activeInput.type === 'answer') {
        console.log('Updating answer at index:', activeInput.index, 'with value:', key);
        handleDigitChange(activeInput.index, key);
      } else if (activeInput.type === 'partial') {
        console.log('Updating partial at index:', activeInput.index, 'row:', activeInput.rowIndex, 'with value:', key);
        handlePartialProductChange(activeInput.rowIndex, activeInput.index, key);
      } else if (activeInput.type === 'carry') {
        console.log('Updating carry at index:', activeInput.index, 'with value:', key);
        handleCarryChange(activeInput.index, key);
      }
      
      // Re-focus the active input field after virtual keyboard input
      setTimeout(() => {
        let inputElement;
        if (activeInput.type === 'partial') {
          // For partial products, the data-index includes rowIndex
          inputElement = document.querySelector(`input[data-index="partial-${activeInput.rowIndex}-${activeInput.index}"]`);
        } else {
          // For answer and carry fields, use the standard format
          inputElement = document.querySelector(`input[data-index="${activeInput.type}-${activeInput.index}"]`);
        }
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    } else {
      console.log('No active input found!');
    }
  };

  const handleVirtualBackspace = () => {
    console.log('Virtual backspace pressed, Active input:', activeInput);
    
    // Use activeInput state which is set when input is focused
    if (activeInput) {
      if (activeInput.type === 'answer') {
        console.log('Clearing answer at index:', activeInput.index);
        const newAnswer = [...userAnswer];
        newAnswer[activeInput.index] = '';
        setUserAnswer(newAnswer);
      } else if (activeInput.type === 'partial') {
        console.log('Clearing partial at index:', activeInput.index, 'row:', activeInput.rowIndex);
        const newPartialProducts = [...partialProducts];
        if (newPartialProducts[activeInput.rowIndex]) {
          newPartialProducts[activeInput.rowIndex][activeInput.index] = '';
          setPartialProducts(newPartialProducts);
        }
      } else if (activeInput.type === 'carry') {
        console.log('Clearing carry at index:', activeInput.index);
        const newCarry = [...carryOver];
        newCarry[activeInput.index] = '';
        setCarryOver(newCarry);
      }
      
      // Re-focus the active input field after virtual keyboard backspace
      setTimeout(() => {
        let inputElement;
        if (activeInput.type === 'partial') {
          // For partial products, the data-index includes rowIndex
          inputElement = document.querySelector(`input[data-index="partial-${activeInput.rowIndex}-${activeInput.index}"]`);
        } else {
          // For answer and carry fields, use the standard format
          inputElement = document.querySelector(`input[data-index="${activeInput.type}-${activeInput.index}"]`);
        }
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  const handleVirtualClear = () => {
    console.log('Virtual clear pressed, Active input:', activeInput);
    
    // Use activeInput state which is set when input is focused
    if (activeInput) {
      if (activeInput.type === 'answer') {
        console.log('Clearing answer at index:', activeInput.index);
        const newAnswer = [...userAnswer];
        newAnswer[activeInput.index] = '';
        setUserAnswer(newAnswer);
      } else if (activeInput.type === 'partial') {
        console.log('Clearing partial at index:', activeInput.index, 'row:', activeInput.rowIndex);
        const newPartialProducts = [...partialProducts];
        if (newPartialProducts[activeInput.rowIndex]) {
          newPartialProducts[activeInput.rowIndex][activeInput.index] = '';
          setPartialProducts(newPartialProducts);
        }
      } else if (activeInput.type === 'carry') {
        console.log('Clearing carry at index:', activeInput.index);
        const newCarry = [...carryOver];
        newCarry[activeInput.index] = '';
        setCarryOver(newCarry);
      }
      
      // Re-focus the active input field after virtual keyboard clear
      setTimeout(() => {
        let inputElement;
        if (activeInput.type === 'partial') {
          // For partial products, the data-index includes rowIndex
          inputElement = document.querySelector(`input[data-index="partial-${activeInput.rowIndex}-${activeInput.index}"]`);
        } else {
          // For answer and carry fields, use the standard format
          inputElement = document.querySelector(`input[data-index="${activeInput.type}-${activeInput.index}"]`);
        }
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Validate answer
  const validateAnswer = () => {
    const multiplicand = parseInt(num1);
    const multiplier = parseInt(num2);
    const expectedResult = multiplicand * multiplier;
    const multiplierDigits = multiplier.toString().split('').reverse();
    
    
    const validation = validateMultiplicationSteps(multiplicand, multiplier, partialProducts, userAnswer, carryOver);
    
    // Store validation errors for UI highlighting
    setValidationErrors(validation.errors || []);
    
    if (validation.isCorrect) {
      setIsCorrect(true);
      setMessage(validation.message); // Use the validation message, not hardcoded
      setScore(prev => prev + 1);
      
      // Track metrics for correct answer
      metricsTracker.trackCheckAnswer('multiplication', gradeLevel, true);
    } else {
      setIsCorrect(false);
      setMessage(validation.message);
      
      // Track metrics for incorrect answer
      metricsTracker.trackCheckAnswer('multiplication', gradeLevel, false);
    }
    
    // Disable Check Answer button after use
    setButtonsState(prev => ({
      ...prev,
      checkAnswerDisabled: true,
      showSolutionDisabled: false
    }));
  };

  // Validate multiplication steps - EXACTLY like Addition
  const validateMultiplicationSteps = (multiplicand, multiplier, userPartialProducts, userAnswer, userCarryOver) => {
    const multiplierDigits = multiplier.toString().split('').reverse();
    const expectedResult = multiplicand * multiplier;
    const userResult = userAnswer.join('');
    
    // FIRST: Check if partial products are correct
    let allPartialProductsCorrect = true;
    for (let i = 0; i < multiplierDigits.length; i++) {
      const digit = parseInt(multiplierDigits[i]);
      const expectedPartialProduct = multiplicand * digit;
      const userPartialProduct = userPartialProducts[i] ? userPartialProducts[i].join('') : '';
      
      if (userPartialProduct !== expectedPartialProduct.toString()) {
        allPartialProductsCorrect = false;
        break;
      }
    }
    
    // SECOND: Check if final answer is correct
    const answerCorrect = userResult === expectedResult.toString();
    
    if (answerCorrect && allPartialProductsCorrect) {
      // Answer is correct, now check carry-over fields
      const carryValidation = validateCarryOverOnly(multiplicand, multiplier, userCarryOver);
      
      if (carryValidation.errors.length === 0) {
        return {
          isCorrect: true,
          message: 'Correct! Well done! 🎉',
          errors: []
        };
      } else {
        // Answer correct but missing carry-over
        return {
          isCorrect: true,
          message: `Correct answer! 🎉 However, you need to practice the complete process:\n\n${carryValidation.errors.map((error, index) => `${index + 1}. ${error.message}`).join('\n')}`,
          errors: carryValidation.errors
        };
      }
    } else {
      // Answer is wrong, show specific errors
      const errors = [];
      
      // Add partial product errors
      for (let i = 0; i < multiplierDigits.length; i++) {
        const digit = parseInt(multiplierDigits[i]);
        const expectedPartialProduct = multiplicand * digit;
        const userPartialProduct = userPartialProducts[i] ? userPartialProducts[i].join('') : '';
        
        if (userPartialProduct !== expectedPartialProduct.toString()) {
          const placeValue = i;
          errors.push({
            step: i + 1,
            type: 'partial_product_value',
            rowIndex: i,
            expected: expectedPartialProduct,
            actual: userPartialProduct,
            message: `Step ${i + 1}: ${multiplicand} × ${digit} = ${expectedPartialProduct}, but you wrote ${userPartialProduct || 'nothing'}. This should be positioned starting from ${getPlaceValueName(placeValue)} position.`
          });
        }
      }
      
      // Add final answer error
      if (!answerCorrect) {
        errors.push({
          step: 'final',
          type: 'final_answer',
          expected: expectedResult,
          actual: userResult,
          message: `Final answer should be ${expectedResult}, but you wrote ${userResult || 'nothing'}`
        });
      }
      
      const errorMessages = errors.map(error => `• ${error.message}`).join('\n');
      return { 
        isCorrect: false, 
        message: `Incorrect:\n${errorMessages}`,
        errors: errors
      };
    }
  };

  // Validate carry-over only - EXACTLY like Addition
  const validateCarryOverOnly = (multiplicand, multiplier, userCarryOver) => {
    const errors = [];
    const multiplierDigits = multiplier.toString().split('').reverse();
    const answerLength = calculateAnswerLength();
    
    // Calculate expected partial products
    const expectedPartialProducts = [];
    for (let i = 0; i < multiplierDigits.length; i++) {
      const digit = parseInt(multiplierDigits[i]);
      const partialProduct = multiplicand * digit;
      const placeValue = i;
      const paddedProduct = partialProduct.toString() + '0'.repeat(placeValue);
      expectedPartialProducts.push(paddedProduct);
    }
    
    // Check carry-over for each position
    let carry = 0;
    for (let pos = 0; pos < answerLength; pos++) {
      let sum = carry;
      
      for (let i = 0; i < expectedPartialProducts.length; i++) {
        const product = expectedPartialProducts[i];
        const digit = product[product.length - 1 - pos] || '0';
        sum += parseInt(digit);
      }
      
      const expectedCarry = Math.floor(sum / 10);
      
      if (expectedCarry > 0) {
        const nextPos = pos + 1;
        if (nextPos < answerLength) {
          const uiIndex = answerLength - 1 - nextPos;
          const userCarry = userCarryOver[uiIndex] || '';
          
          if (userCarry === '' || userCarry === '0') {
            errors.push({
              message: `Carry-over from ${getPlaceValueName(pos)} position should be ${expectedCarry}, but you wrote ${userCarry === '' ? 'not entered' : userCarry}`
            });
          }
        }
      }
      
      carry = expectedCarry;
    }
    
    return { errors };
  };

  // Validate carry-over during partial product addition
  const validateCarryOver = (multiplicand, multiplier, userPartialProducts, userCarryOver) => {
    const errors = [];
    const multiplierDigits = multiplier.toString().split('').reverse();
    const answerLength = calculateAnswerLength();
    
    // Calculate expected partial products with proper place values
    const expectedPartialProducts = [];
    for (let i = 0; i < multiplierDigits.length; i++) {
      const digit = parseInt(multiplierDigits[i]);
      const partialProduct = multiplicand * digit;
      const placeValue = i;
      // Add zeros at the end to align with place values (multiply by 10^placeValue)
      const paddedProduct = partialProduct.toString() + '0'.repeat(placeValue);
      expectedPartialProducts.push(paddedProduct);
    }
    
    // Calculate expected final result by adding all partial products
    const expectedResult = multiplicand * multiplier;
    const expectedResultStr = expectedResult.toString().padStart(answerLength, '0');
    
    // Validate carry-over for each position
    let carry = 0;
    for (let pos = 0; pos < answerLength; pos++) {
      let sum = carry;
      
      // Add digits from all partial products at this position
      for (let i = 0; i < expectedPartialProducts.length; i++) {
        const product = expectedPartialProducts[i];
        const digit = product[product.length - 1 - pos] || '0';
        sum += parseInt(digit);
      }
      
      // Calculate expected carry for THIS position (not next position)
      const expectedCarry = Math.floor(sum / 10);
      
      // Only show carry-over error if there actually should be a carry AND user entered something
      if (expectedCarry > 0) {
        // The carry from this position goes to the next position (pos + 1)
        // So we check the carry-over input at the next position
        const nextPos = pos + 1;
        if (nextPos < answerLength) {
          const uiIndex = answerLength - 1 - nextPos;
          const userCarry = parseInt(userCarryOver[uiIndex] || '');
          if (isNaN(userCarry) || userCarry !== expectedCarry) {
            const actualValue = isNaN(userCarry) ? 'not entered' : userCarry;
            errors.push({
              step: 'carry',
              type: 'carry_over',
              position: pos,
              expected: expectedCarry,
              actual: userCarry,
              message: `Carry-over from ${getPlaceValueName(pos)} position should be ${expectedCarry}, but you wrote ${actualValue}`
            });
          }
        }
      } else {
        // If no carry is expected from this position, check if user incorrectly entered a carry
        const nextPos = pos + 1;
        if (nextPos < answerLength) {
          const uiIndex = answerLength - 1 - nextPos;
          const userCarry = parseInt(userCarryOver[uiIndex] || '');
          if (!isNaN(userCarry) && userCarry > 0) {
            errors.push({
              step: 'carry',
              type: 'carry_over',
              position: pos,
              expected: 0,
              actual: userCarry,
              message: `No carry-over needed from ${getPlaceValueName(pos)} position, but you wrote ${userCarry}`
            });
          }
        }
      }
      
      carry = expectedCarry;
    }
    
    return errors;
  };

  // Helper function to convert place value numbers to names
  const getPlaceValueName = (placeValue) => {
    const placeNames = {
      0: 'ones',
      1: 'tens', 
      2: 'hundreds',
      3: 'thousands',
      4: 'ten thousands',
      5: 'hundred thousands',
      6: 'millions',
      7: 'ten millions',
      8: 'hundred millions'
    };
    return placeNames[placeValue] || `${placeValue}th place`;
  };

  // Check if a specific input has an error
  const hasError = (type, rowIndex, colIndex) => {
    if (isCorrect === null) return false; // No errors shown until validation
    
    if (type === 'partial' && rowIndex !== undefined) {
      return validationErrors.some(error => 
        (error.type === 'partial_product_value' || error.type === 'partial_product_position') && error.rowIndex === rowIndex
      );
    }
    
    if (type === 'carry' && colIndex !== undefined) {
      return validationErrors.some(error => 
        error.type === 'carry_over' && error.position === colIndex
      );
    }
    
    if (type === 'answer') {
      return validationErrors.some(error => error.type === 'final_answer');
    }
    
    return false;
  };

  // Show solution steps
  const showSolutionSteps = () => {
    setShowSolution(true);
    setSolutionSteps(getSolutionSteps());
    
    // Track metrics for show solution
    metricsTracker.trackShowSolution('multiplication', gradeLevel);
  };

  // Generate solution steps for long multiplication
  const getSolutionSteps = () => {
    const multiplicand = parseInt(num1);
    const multiplier = parseInt(num2);
    const multiplierDigits = multiplier.toString().split('').reverse();
    const steps = [];
    
    let partialProducts = [];
    
    // Generate partial products with proper place values
    for (let i = 0; i < multiplierDigits.length; i++) {
      const digit = parseInt(multiplierDigits[i]);
      const partialProduct = multiplicand * digit; // No carry needed for individual digit multiplication
      
      // Calculate place value for this partial product
      const placeValue = i; // 0 = ones, 1 = tens, 2 = hundreds, etc.
      const partialProductWithZeros = partialProduct * Math.pow(10, placeValue);
      
      steps.push({
        step: i + 1,
        multiplierDigit: digit,
        calculation: `${multiplicand} × ${digit} = ${partialProduct}`,
        partialProduct: partialProduct,
        position: i,
        placeValue: placeValue,
        partialProductWithZeros: partialProductWithZeros,
        explanation: `Step ${i + 1}: Multiply ${multiplicand} by ${digit} (${placeValue === 0 ? 'ones' : placeValue === 1 ? 'tens' : placeValue === 2 ? 'hundreds' : `${placeValue}th place`})`
      });
      
      partialProducts.push({
        value: partialProductWithZeros,
        original: partialProduct,
        placeValue: placeValue
      });
    }
    
    // Calculate final result by adding all partial products
    const finalResult = partialProducts.reduce((sum, partial) => sum + partial.value, 0);
    
    return {
      steps: steps,
      partialProducts: partialProducts,
      result: finalResult
    };
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, currentIndex, type, rowIndex = null) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      validateAnswer();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      // Move to next input
      const nextIndex = currentIndex + 1;
      const maxLength = calculateAnswerLength();
      
      if (nextIndex < maxLength) {
        let nextInput;
        if (type === 'partial') {
          // For partial products, the data-index includes rowIndex
          nextInput = document.querySelector(`[data-index="partial-${rowIndex}-${nextIndex}"]`);
        } else {
          // For answer and carry fields, use the standard format
          nextInput = document.querySelector(`[data-index="${type}-${nextIndex}"]`);
        }
        if (nextInput) nextInput.focus();
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      // Move to previous input
      const prevIndex = currentIndex - 1;
      
      if (prevIndex >= 0) {
        let prevInput;
        if (type === 'partial') {
          // For partial products, the data-index includes rowIndex
          prevInput = document.querySelector(`[data-index="partial-${rowIndex}-${prevIndex}"]`);
        } else {
          // For answer and carry fields, use the standard format
          prevInput = document.querySelector(`[data-index="${type}-${prevIndex}"]`);
        }
        if (prevInput) prevInput.focus();
      }
    }
  };

  // Generate initial problem on component mount
  useEffect(() => {
    generateProblem(true); // Pass true for initial load
  }, [gradeLevel]);

  // Click outside to hide keyboard
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mathProblemRef.current && !mathProblemRef.current.contains(event.target)) {
        setKeyboardVisible(false);
        setActiveInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Pad number with spaces for display
  const padNumber = (num, length) => {
    return num.toString().padStart(length, ' ');
  };

  const maxLength = Math.max((num1 || '').length, (num2 || '').length);
  const answerLength = calculateAnswerLength();
  const multiplierDigits = num2 ? num2.split('').reverse() : [];

  return (
    <div className="multiplication-practice">
      {/* 1. Operation Practice (Title + Description) */}
      <div className="page-title">
        <h2>Multiplication Practice</h2>
        <p>Practice long multiplication with step-by-step working and partial products</p>
      </div>



      {/* Score Display */}
      <div className="stats">
        <div className="stat-item">
          <div className="stat-value">{score}</div>
          <div className="stat-label">Score</div>
        </div>
      </div>

      {/* Grade Level, Custom Number Options, and Generate Button */}
      <div className="options-section">
        <div className="grade-selector">
          <label htmlFor="gradeLevel">Level:</label>
          <select 
            id="gradeLevel" 
            value={gradeLevel} 
            onChange={(e) => {
              setGradeLevel(e.target.value);
              // Track level selection when user clicks dropdown
              metricsTracker.trackLevelSelected('multiplication', e.target.value);
            }}
            style={{ marginLeft: '10px', padding: '5px', borderRadius: '4px' }}
          >
            {Array.from({ length: 8 }, (_, i) => i + 1).map(level => (
              <option 
                key={level} 
                value={level.toString()}
                disabled={level > 3}
                style={{ color: level > 3 ? '#999' : 'inherit' }}
              >
                {level > 3 ? `Level ${level} (Upto ${level + 1} digits) - Upgrade to Pro Edition` : `Level ${level} (Upto ${level + 1} digits)`}
              </option>
            ))}
          </select>
        </div>

        <div className="custom-numbers">
          <label>
            <input 
              type="checkbox" 
              checked={useCustomNumbers}
              onChange={(e) => setUseCustomNumbers(e.target.checked)}
            />
            Use Custom Numbers
          </label>
          {useCustomNumbers && (
            <div className="custom-inputs" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="number"
                placeholder="Multiplicand"
                value={customNumbers.num1}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 4) {
                      setCustomNumbers(prev => ({ ...prev, num1: value }));
                    }
                  }}
                  style={{ padding: '5px', borderRadius: '4px' }}
              />
              <input
                type="number"
                placeholder="Multiplier"
                value={customNumbers.num2}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 4) {
                      setCustomNumbers(prev => ({ ...prev, num2: value }));
                    }
                  }}
                style={{ padding: '5px', borderRadius: '4px' }}
              />
              </div>
              {(customNumbers.num1.length > 4 || customNumbers.num2.length > 4) && (
                <div style={{ 
                  fontSize: '0.8rem', 
                  color: '#e74c3c', 
                  padding: '4px 8px',
                  backgroundColor: '#fdf2f2',
                  borderRadius: '4px',
                  border: '1px solid #fecaca'
                }}>
                  ⚠️ Upgrade to Pro Edition to use numbers with more than 4 digits
                </div>
              )}
            </div>
          )}
        </div>

        <button className="btn btn-primary" onClick={generateProblem}>
          Generate New Problem
        </button>
      </div>

      {/* Question Number */}
      <div className="question-number">
        Question #{questionNumber}: {num1}×{num2}=?
      </div>

      {/* Problem Section */}
      <div className="problem-section" style={{ marginLeft: '200px' }}> {/* Move problem to the right */}
        {/* Math Problem */}
        <div className="math-problem" ref={mathProblemRef} style={{ position: 'relative' }}>
          {/* Long Multiplication Layout - Paper-Pencil Style with Right Alignment */}
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            fontFamily: 'monospace',
            fontSize: '18px',
            alignItems: 'flex-end', // Right align everything
            marginRight: '200px' // Reduced margin for better spacing
          }}>
           {/* Multiplicand row */}
           <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
             <div style={{ width: '30px' }}></div> {/* Space for operator */}
             {padNumber(num1, maxLength).split('').map((digit, index) => (
               <div
                 key={`num1-${index}`}
                 style={{
                   width: '40px',
                   height: '50px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   backgroundColor: '#e8f4fd',
                   border: '2px solid #ddd',
                   borderRadius: '4px',
                   fontWeight: 'bold'
                 }}
               >
                 {digit === ' ' ? '' : digit}
               </div>
             ))}
           </div>

           {/* Multiplier row with operator */}
           <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
             <div style={{ 
               width: '30px', 
               textAlign: 'center',
               fontSize: '24px',
               fontWeight: 'bold'
             }}>×</div>
             {padNumber(num2, maxLength).split('').map((digit, index) => (
               <div
                 key={`num2-${index}`}
                 style={{
                   width: '40px',
                   height: '50px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   backgroundColor: '#e8f4fd',
                   border: '2px solid #ddd',
                   borderRadius: '4px',
                   fontWeight: 'bold'
                 }}
               >
                 {digit === ' ' ? '' : digit}
               </div>
             ))}
           </div>

           {/* Line */}
           <div style={{ 
             height: '2px', 
             backgroundColor: '#333',
             margin: '10px 0',
             width: `${maxLength * 40 + (maxLength - 1) * 5}px` // Width based on multiplicand
           }}></div>

           {/* Single Carry-over row above first partial product */}
           {multiplierDigits.length > 0 && (
             <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
               <div style={{ 
                 width: '60px', 
                 textAlign: 'center',
                 fontSize: '12px',
                 fontWeight: 'bold',
                 color: '#e67e22'
               }}>Carry</div> {/* Label for Carry */}
               {Array.from({ length: answerLength }, (_, colIndex) => (
                 <div key={`carry-${colIndex}`} style={{ width: '40px', height: '25px' }}>
                   {colIndex < answerLength - 1 && (
                     <input
                       type="text"
                       className={`carry-input ${hasError('carry', null, colIndex) ? 'error' : ''}`}
                       value={carryOver[colIndex] || ''}
                       onChange={(e) => handleCarryChange(colIndex, e.target.value)}
                       onFocus={(e) => handleInputFocus(colIndex, 'carry')}
                       onBlur={handleInputBlur}
                       onKeyDown={(e) => handleKeyDown(e, colIndex, 'carry')}
                       data-index={`carry-${colIndex}`}
                       data-type="carry"
                       placeholder=""
                       style={{
                         width: '30px',
                         height: '20px',
                         fontSize: '12px',
                         textAlign: 'center',
                         border: 'none',
                         borderBottom: hasError('carry', null, colIndex) ? '2px solid #e74c3c' : '2px solid #e67e22',
                         borderRadius: '0',
                         backgroundColor: hasError('carry', null, colIndex) ? '#ffeaea' : '#fff3e0',
                         color: hasError('carry', null, colIndex) ? '#e74c3c' : '#e67e22',
                         fontWeight: 'bold'
                       }}
                     />
                   )}
                 </div>
               ))}
             </div>
           )}

           {/* Partial Products Rows - Right Aligned with Diagonal Growth */}
           {multiplierDigits.map((digit, rowIndex) => {
             const leftPadding = rowIndex * 40; // Each row shifts left by one digit width
             return (
               <div key={`partial-row-${rowIndex}`} style={{ 
                 display: 'flex', 
                 alignItems: 'center', 
                 gap: '5px',
                 marginLeft: `${leftPadding}px` // Shift left for diagonal growth
               }}>
                 <div style={{ width: '30px' }}></div> {/* Space for operator */}
                 {Array.from({ length: answerLength }, (_, colIndex) => (
                   <div key={`partial-${rowIndex}-${colIndex}`} style={{ width: '40px', height: '50px' }}>
                     <input
                       type="text"
                       className={`partial-input ${hasError('partial', rowIndex, colIndex) ? 'error' : ''}`}
                       value={partialProducts[rowIndex] && partialProducts[rowIndex][colIndex] ? partialProducts[rowIndex][colIndex] : ''}
                       onChange={(e) => handlePartialProductChange(rowIndex, colIndex, e.target.value)}
                       onFocus={(e) => handleInputFocus(colIndex, 'partial', rowIndex)}
                       onBlur={handleInputBlur}
                       onKeyDown={(e) => handleKeyDown(e, colIndex, 'partial', rowIndex)}
                       data-index={`partial-${rowIndex}-${colIndex}`}
                       data-type="partial"
                       placeholder=""
                       style={{
                         width: '40px',
                         height: '50px',
                         textAlign: 'center',
                         fontSize: '18px',
                         fontWeight: 'bold',
                         border: hasError('partial', rowIndex, colIndex) ? '2px solid #e74c3c' : '2px solid #ddd',
                         borderRadius: '4px',
                         backgroundColor: hasError('partial', rowIndex, colIndex) ? '#ffeaea' : 'white'
                       }}
                     />
                   </div>
                 ))}
               </div>
             );
           })}

           {/* Final line */}
           <div style={{ 
             height: '2px', 
             backgroundColor: '#333',
             margin: '10px 0',
             width: `${answerLength * 40 + (answerLength - 1) * 5}px` // Width based on final answer
           }}></div>

           {/* Answer row */}
           <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
             <div style={{ width: '30px' }}></div> {/* Space for operator */}
             {Array.from({ length: answerLength }, (_, index) => (
               <div key={`answer-container-${index}`} style={{ 
                 width: '40px', 
                 height: '50px',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 position: 'relative'
               }}>
                 <input
                   key={`answer-${index}`}
                   type="text"
                   className={`digit-input ${hasError('answer', null, index) ? 'error' : ''}`}
                   value={userAnswer[index] || ''}
                   onChange={(e) => handleDigitChange(index, e.target.value)}
                   onFocus={(e) => handleInputFocus(index, 'answer')}
                   onBlur={handleInputBlur}
                   onKeyDown={(e) => handleKeyDown(e, index, 'answer')}
                   data-index={`answer-${index}`}
                   data-type="answer"
                   maxLength={1}
                   placeholder=""
                   style={{
                     width: '40px',
                     height: '50px',
                     textAlign: 'center',
                     fontSize: '18px',
                     fontWeight: 'bold',
                     border: hasError('answer', null, index) ? '2px solid #e74c3c' : '2px solid #ddd',
                     borderRadius: '4px',
                     backgroundColor: hasError('answer', null, index) ? '#ffeaea' : 'transparent',
                     transition: 'border-color 0.3s ease',
                     outline: 'none'
                   }}
                 />
                 {/* Dash underline */}
                 <div style={{
                   position: 'absolute',
                   bottom: '5px',
                   left: '50%',
                   transform: 'translateX(-50%)',
                   width: '25px',
                   height: '3px',
                   backgroundColor: '#333',
                   borderRadius: '1px'
                 }}></div>
               </div>
             ))}
           </div>
         </div>

         {/* Virtual Keyboard */}
         <VirtualKeyboard
           onKeyPress={handleVirtualKeyPress}
           onBackspace={handleVirtualBackspace}
           onClear={handleVirtualClear}
           position={keyboardPosition}
           isVisible={keyboardVisible}
         />
       </div>

         {/* Result Message */}
         {message && (
           <div className={`result ${isCorrect ? 'success' : 'error'}`} style={{ 
             whiteSpace: 'pre-line', 
             textAlign: 'left',
             padding: '10px',
             margin: '10px 0',
             borderRadius: '4px',
             backgroundColor: isCorrect ? '#d4edda' : '#f8d7da',
             border: `1px solid ${isCorrect ? '#c3e6cb' : '#f5c6cb'}`,
             color: isCorrect ? '#155724' : '#721c24'
           }}>
             {message}
           </div>
         )}

         {/* Controls */}
         <div className="controls">
           <button 
             className="btn btn-success" 
             onClick={validateAnswer}
             disabled={buttonsState.checkAnswerDisabled}
             style={{
               opacity: buttonsState.checkAnswerDisabled ? 0.6 : 1,
               cursor: buttonsState.checkAnswerDisabled ? 'not-allowed' : 'pointer'
             }}
           >
             {buttonsState.checkAnswerDisabled ? 'Already Checked' : 'Check Answer'}
           </button>
           <button 
             className="btn btn-warning" 
             onClick={showSolutionSteps}
             disabled={buttonsState.showSolutionDisabled}
             style={{
               opacity: buttonsState.showSolutionDisabled ? 0.6 : 1,
               cursor: buttonsState.showSolutionDisabled ? 'not-allowed' : 'pointer'
             }}
           >
             Show Solution
           </button>
         </div>
       </div>

                               {/* Solution Section - At the Bottom */}
         {showSolution && solutionSteps && (
           <div className="solution-section" style={{ 
             marginTop: '30px', 
             padding: '20px', 
             backgroundColor: '#f8f9fa', 
             borderRadius: '8px', 
             border: '1px solid #e9ecef',
             marginLeft: '200px',
             marginRight: '200px'
           }}>
             <h3 style={{ color: '#667eea', marginBottom: '15px' }}>Step-by-Step Solution</h3>
             <div className="solution-steps">
               {/* Problem and Steps Combined */}
               <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                 <strong style={{ color: '#667eea' }}>Problem: {num1} × {num2} = ?</strong>
                 <div style={{ marginTop: '10px', fontFamily: 'monospace', fontSize: '14px' }}>
                   {solutionSteps.steps.map((step, index) => (
                     <div key={index} style={{ marginBottom: '5px' }}>
                       <span style={{ color: '#333', backgroundColor: '#e8f4fd', padding: '2px 4px', borderRadius: '3px' }}>{step.calculation}</span>
                       <span style={{ color: '#155724', backgroundColor: '#d4edda', padding: '2px 4px', borderRadius: '3px' }}> = {step.partialProduct}</span>
                       <span style={{ color: '#666', fontSize: '12px' }}> ({step.explanation.split(': ')[1]})</span>
                     </div>
                   ))}
                 </div>
               </div>

               {/* Partial Products and Addition Combined */}
               <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                 <strong style={{ color: '#667eea' }}>Partial Products with Place Values:</strong>
                 <div style={{ marginTop: '10px', fontFamily: 'monospace', fontSize: '14px' }}>
                   {solutionSteps.partialProducts.map((partial, index) => (
                     <div key={index} style={{ marginBottom: '3px', textAlign: 'right' }}>
                       {partial.value.toString().padStart(solutionSteps.result.toString().length, ' ')}
                       <span style={{ color: '#666', fontSize: '12px', marginLeft: '10px' }}>
                         ({partial.original} × 10^{partial.placeValue})
                       </span>
                     </div>
                   ))}
                   <div style={{ 
                     borderTop: '2px solid #333', 
                     marginTop: '5px', 
                     paddingTop: '5px',
                     textAlign: 'right',
                     fontWeight: 'bold'
                   }}>
                     {solutionSteps.result}
                   </div>
                 </div>
               </div>

               {/* Final Answer */}
               <div style={{ padding: '12px', backgroundColor: '#667eea', color: 'white', borderRadius: '6px', fontWeight: 'bold', textAlign: 'center' }}>
                 Final Answer: {solutionSteps.result}
               </div>
             </div>
           </div>
         )}
     </div>
  );
};

export default Multiplication; 